import { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Button,
} from '@openzeppelin/ui-components';
import { Icon } from '@ascend/shared/components';
import { SearchBar } from '../components/ListControls';

// ─── Types ────────────────────────────────────────────────────────────────────

interface LendingPosition {
  id: string;
  poolName: string;
  principal: number;
  earnedYield: number;
  apy: string;
}

// ─── Mock Data ────────────────────────────────────────────────────────────────

const POSITIONS: LendingPosition[] = [
  { id: 'pool-1', poolName: 'USDC Lending Pool', principal: 50000, earnedYield: 1200, apy: '8.5%' },
  { id: 'pool-2', poolName: 'XSGD Lending Pool', principal: 25000, earnedYield: 600, apy: '7.2%' },
  { id: 'pool-3', poolName: 'USDC Prime Pool', principal: 75000, earnedYield: 1530, apy: '6.8%' },
  { id: 'pool-4', poolName: 'DAI Stable Pool', principal: 30000, earnedYield: 495, apy: '5.5%' },
  { id: 'pool-5', poolName: 'USDC Growth Pool', principal: 40000, earnedYield: 1104, apy: '9.2%' },
  { id: 'pool-6', poolName: 'DAI Income Pool', principal: 60000, earnedYield: 1404, apy: '7.8%' },
  { id: 'pool-7', poolName: 'USDT Reserve Pool', principal: 20000, earnedYield: 366, apy: '6.1%' },
  { id: 'pool-8', poolName: 'ABN Yield Pool', principal: 15000, earnedYield: 472, apy: '10.5%' },
  { id: 'pool-9', poolName: 'FMM Opportunity Pool', principal: 35000, earnedYield: 934, apy: '8.9%' },
  { id: 'pool-10', poolName: 'MSM Capital Pool', principal: 45000, earnedYield: 1012, apy: '7.5%' },
  { id: 'pool-11', poolName: 'SBC Lending Pool', principal: 10000, earnedYield: 336, apy: '11.2%' },
  { id: 'pool-12', poolName: 'Meridian Income Pool', principal: 55000, earnedYield: 1072, apy: '6.5%' },
];

function fmt(value: number): string {
  return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// ─── Page Component ───────────────────────────────────────────────────────────

export function LenderWithdrawPage() {
  const [searchParams] = useSearchParams();
  const [selectedPositionId, setSelectedPositionId] = useState<string | null>(
    searchParams.get('poolId'),
  );
  const [amount, setAmount] = useState('');
  const [isWithdrawing, setIsWithdrawing] = useState(false);
  const [search, setSearch] = useState('');

  const pos = POSITIONS.find((p) => p.id === selectedPositionId);
  const parsedAmount = parseFloat(amount) || 0;
  const maxAmount = pos ? pos.principal + pos.earnedYield : 0;

  const filteredPositions = useMemo(
    () => POSITIONS.filter((p) => p.poolName.toLowerCase().includes(search.toLowerCase())),
    [search],
  );

  const withdrawingPrincipal = pos ? Math.min(parsedAmount, pos.principal) : 0;
  const claimingYield = pos ? Math.max(0, parsedAmount - pos.principal) : 0;

  function handleMax() {
    if (pos) setAmount(maxAmount.toString());
  }

  function handleWithdraw() {
    if (!pos) return;
    if (parsedAmount <= 0) { toast.error('Please enter a valid amount'); return; }
    if (parsedAmount > maxAmount) { toast.error('Amount exceeds available balance'); return; }
    setIsWithdrawing(true);
    setTimeout(() => {
      setIsWithdrawing(false);
      setAmount('');
      setSelectedPositionId(null);
      toast.success(`Successfully withdrew ${fmt(parsedAmount)} from ${pos.poolName}`);
    }, 2000);
  }

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Page header */}
      <div className="flex items-start gap-3">
        <Icon name="streamline:interface-arrows-button-to-top-arrow-up-line-to-top" className="mt-1 h-6 w-6 sm:h-7 sm:w-7 text-foreground flex-shrink-0" />
        <div>
          <h1 className="text-xl sm:text-2xl font-bold">Withdraw Stablecoins</h1>
          <p className="text-sm text-muted-foreground">
            Withdraw your deposited stablecoins and earned yield.
          </p>
        </div>
      </div>

      {!pos ? (
        <>
          <SearchBar value={search} onChange={setSearch} placeholder="Search positions..." className="sm:w-64" />

          {filteredPositions.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">No positions found matching your search.</p>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {filteredPositions.map((p) => (
                <Card key={p.id}>
                  <CardHeader className="p-6">
                    <CardTitle>{p.poolName}</CardTitle>
                    <CardDescription>Your current deposit in this pool.</CardDescription>
                  </CardHeader>
                  <CardContent className="p-6 pt-0 space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Principal</span>
                      <span className="font-semibold">{fmt(p.principal)}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Earned Yield</span>
                      <span className="font-semibold text-ascend-success">+{fmt(p.earnedYield)}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">APY</span>
                      <span className="rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-700">
                        {p.apy}
                      </span>
                    </div>
                    <Button className="w-full" onClick={() => setSelectedPositionId(p.id)}>
                      Select
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </>
      ) : (
        <Card className="mx-auto max-w-lg">
          <CardHeader className="p-6 pb-0">
            <CardTitle>{pos.poolName}</CardTitle>
            <CardDescription>
              Withdraw your deposited stablecoins and earned yield from this pool.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            {/* Stat cards */}
            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-xl border border-border p-5">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Principal</span>
                  <Icon name="streamline:money-wallet-money-payment-finance-wallet" className="h-4 w-4 text-muted-foreground" />
                </div>
                <p className="mt-2 text-2xl font-bold">{fmt(pos.principal)}</p>
              </div>
              <div className="rounded-xl border border-border p-5">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Earned Yield</span>
                  <Icon name="streamline:graph-bar-increase" className="h-4 w-4 text-ascend-success" />
                </div>
                <p className="mt-2 text-2xl font-bold text-ascend-success">+{fmt(pos.earnedYield)}</p>
              </div>
            </div>

            {/* Amount input */}
            <div className="rounded-xl border border-border p-5 space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Amount to Withdraw</span>
                <button
                  type="button"
                  onClick={handleMax}
                  className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Icon name="streamline:money-wallet-money-payment-finance-wallet" className="h-3.5 w-3.5" />
                  Max: {fmt(maxAmount)}
                </button>
              </div>
              <div className="flex items-center gap-2 rounded-lg border border-input bg-background px-4 py-3 focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 ring-offset-background">
                <span className="text-lg text-muted-foreground">$</span>
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  min="0"
                  step="any"
                  className="flex-1 bg-transparent text-lg font-medium outline-none placeholder:text-muted-foreground [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [-moz-appearance:textfield]"
                />
                <span className="rounded-md bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground">USDC</span>
              </div>

              {/* Breakdown */}
              {parsedAmount > 0 && (
                <div className="rounded-lg bg-muted/50 p-4 space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Withdrawing Principal</span>
                    <span className="font-semibold">{fmt(withdrawingPrincipal)}</span>
                  </div>
                  {claimingYield > 0 && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Claiming Yield</span>
                      <span className="font-semibold text-ascend-success">+{fmt(claimingYield)}</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="grid grid-cols-2 gap-4">
              <Button
                variant="outline"
                onClick={() => { setSelectedPositionId(null); setAmount(''); }}
              >
                Cancel
              </Button>
              <Button
                disabled={isWithdrawing || parsedAmount <= 0 || parsedAmount > maxAmount}
                onClick={handleWithdraw}
              >
                {isWithdrawing ? (
                  <>
                    <Icon name="streamline:interface-page-controller-loading-1-progress-loading-load-wait-waiting" className="mr-2 h-4 w-4 animate-spin" />
                    Withdrawing...
                  </>
                ) : (
                  <>
                    <Icon name="streamline:interface-arrows-button-to-bottom-arrow-down-line-to-bottom" className="mr-2 h-4 w-4" />
                    Withdraw
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
